import {adminClient} from 'helper/httpClient';
import {
    useSWRPaginationTemplate,
    useSWRTemplate,
} from 'hooks/dataHooksTemplate';
import {cleanObjectNilValue} from 'utils/clean';
import {get, isNil, keyBy, keys, some, split} from 'lodash';
import {sleep_} from '@genx/july/lib/commonjs/lang';
import {useCallback, useMemo} from 'react';
import {allPermissions, permissionSettings} from 'constant/permissions';
import {processUserProfile} from 'data/userProfile';
import { api } from './common';
import { useAuth } from 'hooks/useAuth';
import { useTranslation } from 'react-i18next';

const processData = (data, allPermissions, parentRowKey, t) => {
        const processedData = allPermissions.map((item, index) => {
            const itemData = data?.find((i) => i.code === item.code);
            const key = item.code;
            const rowKey = `${parentRowKey ? `${parentRowKey}.` : ''}${
                item.code
            }`;
            return {
                ...item,
                read: itemData?.read,
                write: itemData?.write||false,
                key: key,
                label: t(key),
                rowKey,
                children: item.children
                    ? processData(itemData?.children, item.children, rowKey, t)
                    : null,
            };
        });
        return processedData;
    };

export const useAllPermissions = (data) => {
    const {t} = useTranslation('app');
    const swr = useSWRTemplate({
        key: ['allSystemPermissions', data],
        request: async ([_]) => {
            const {result} = await api().get(['permissions'], { role: 'ADMIN' })
            // await sleep_(1000);
            return processData(data, result, null, t);
            return allPermissions
        },
        defaultValue: [],
    });
    return swr;
};

const processSub = (item) => {
    if (item?.children) {
        return {
            ...item,
            children: keyBy(item.children.map(processSub), 'code'),
        };
    } else {
        return item;
    }
};

export const useAdminPermissionSettings = (options = {}) => {
    const {roles = []} = options;
    const {isUser} = useAuth()
    const swr = useSWRTemplate({
        key: roles?.length && !isUser ? ['permissionSettings']: null,
        request: async ([_]) => {
            // await sleep_(1000);
            let data = {}
            for (const key of roles) {
                const {result} = await adminClient.get(['permissions'], { role: key })
                data[key] = result
            }
            return data
        },
        defaultValue: {},
        // config: {
        //     revalidateOnFocus: false,
        //     revalidateOnReconnect: false,
        //     refreshInterval:0
        // },
    });
    // const swr = {
    //     data: permissionSettings,
    //     isLoading: false,
    //     isValidating:false,
    //     error:null
    // };
    const searchOptPermission = useMemo(
        () =>
            keys(get(swr, 'data', {})).reduce((prev, curr) => {
                return {
                    ...prev,
                    [curr]: keyBy(
                        get(swr, `data.${curr}`, []).map(processSub),
                        'code',
                    ),
                };
            }, {}),
        [swr.data],
    );
    return {
        ...swr,
        getPermission: useCallback(
            (key, type = 'read') => {
                return some(
                    roles.map((role) => {
                        const setting = get(searchOptPermission, role);
                        if (isNil(setting)) {
                            return false;
                        }
                        const splitKeys = split(key, '.');
                        const permission = get(
                            setting,
                            `${splitKeys.join('.children.')}.${type}`,
                            false,
                        );
                        return permission;
                    }),
                );
            },
            [searchOptPermission],
        ),
    };
};

export const useSystemRoles = () => {
    const swr = useSWRTemplate({
        key: ['adminSystemRoles'],
        request: async ([_]) => {
            const {result} = await adminClient.get(['role-type']);
            return result.filter((item) => !['DEVELOPER', 'IT_ADMIN'].includes(item.code));
        },
        defaultValue: [],
    });
    return swr;
};

export const useSystemRolesAccessByCode = (role) => {
    const swr = useSWRTemplate({
        key: ['adminSystemRolesAccess',role],
        request: async ([_]) => {
            const {result} = await adminClient.get(['permissions',role])
            return result
        },
        defaultValue: {},
    });
    return swr;
};

export const useSystemRolesTreeByCode = (role) => {
    const swr = useSWRTemplate({
        key: role ? ['adminSystemTreeAccess',role] : null,
        request: async ([_]) => {
            const {result} = await adminClient.get(['admin-role-tree'], cleanObjectNilValue({role}))
            return result
        },
        defaultValue: [],
    });
    return swr;
};

export const useSystemRolesAccess = (role) => {
    const swr = useSWRTemplate({
        key: ['adminSystemRolesAccess',role],
        request: async ([_]) => {
            // await sleep_(1000);
            // return permissionSettings;
            const {result} = await adminClient.get(['permissions'], { role: role })
            return result
        },
        defaultValue: {},
    });
    return swr;
};

export const updateRolePermission = async (code, data) => {
    const result = await adminClient.put(['permissions', code], data)
    return result
}

export const createRolePermission = async (data) => {
    const result = await adminClient.post(['permissions'], data)
    return result
}

export const useAdminSystemUsers = (options = {}) => {
    const {username} = options;
    const swr = useSWRPaginationTemplate({
        key: ['adminSysmteUsers', username],
        request: async (_key, _pageIndex, _pageSize) => {
            const [_, _username] = _key;
            const {result} = await adminClient.get(['staff'], {
                username: _username,
                pageIndex: _pageIndex - 1,
                pageSize: _pageSize,
            });
            return {
                result: {
                    totalItems: get(result, 'totalItems', 0),
                    items: get(result, 'items', []).map(processUserProfile),
                },
            };
        },
        defaultValue: [],
    });
    return swr;
};


export const editRoleTree = async (id, data) => {
    const {result} = await api().put(['admin-role-tree', id], data);
};


export const useRolesList = (role) => {
    const swr = useSWRTemplate({
        key: role ? ['roleList',role] : null,
        request: async ([_]) => {
            const {result} = await adminClient.get(['role-list'], cleanObjectNilValue({role}))
            return result
        },
        defaultValue: [],
    });
    return swr;
};